import "@/App.css";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import SEO, { SITE_URL } from "./components/SEO";
import { PLAN_LOW_PRICE } from "./data/content";
import { ORGANIZATION_NODE } from "./lib/schema";
import Navbar from "./components/sections/Navbar";
import Hero from "./components/sections/Hero";
import FeatureGrid from "./components/sections/FeatureGrid";
import WhiteLabel from "./components/sections/WhiteLabel";
import HowAgenciesEarn from "./components/sections/HowAgenciesEarn";
import FAQ from "./components/sections/FAQ";
import CredibilityStrip from "./components/sections/CredibilityStrip";
import Footer from "./components/sections/Footer";
import PricingPage from "./components/sections/PricingPage";
import PricingPlanDetailPage from "./components/sections/PricingPlanDetailPage";
import PlansPage from "./components/plans/PlansPage";
import PlanDetailPage from "./components/plans/PlanDetailPage";
import LifetimeDealPage from "./components/sections/LifetimeDealPage";
// Signup/login are now handled on the product subdomain (post.magicbolt.in) -
// internal pages kept for reference, routes disabled below.
// import SignupPage from "./components/sections/SignupPage";
// import LoginPage from "./components/sections/LoginPage";
import FeaturesPage from "./components/sections/FeaturesPage";
import FeatureDetailPage from "./components/sections/FeatureDetailPage";
import WhiteLabelPage from "./components/sections/WhiteLabelPage";
import HowAgenciesEarnPage from "./components/sections/HowAgenciesEarnPage";
import ExamineCustomerPage from "./components/sections/ExamineCustomerPage";
import FaqPage from "./components/sections/FaqPage";
import ProductFinderPage from "./components/sections/ProductFinderPage";
import AboutPage from "./components/sections/AboutPage";
import ContactPage from "./components/sections/ContactPage";
import PrivacyPolicyPage from "./components/sections/PrivacyPolicyPage";
import TermsPage from "./components/sections/TermsPage";
import RefundPolicyPage from "./components/sections/RefundPolicyPage";
import NotFoundPage from "./components/sections/NotFoundPage";
import WhatsAppButton from "./components/WhatsAppButton";
import CookieConsent from "./components/CookieConsent";

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

const ORGANIZATION_JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    // Organization + LocalBusiness, shared with /about (src/lib/schema.js).
    ORGANIZATION_NODE,
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      name: "MagicBolt AI",
      url: SITE_URL,
      publisher: { "@id": `${SITE_URL}/#organization` },
      description:
        "White-label AI automation, AEO/GEO optimization and QR software platform for agencies to resell under their own brand.",
    },
    {
      "@type": "SoftwareApplication",
      "@id": `${SITE_URL}/#software`,
      name: "MagicBolt AI",
      applicationCategory: "BusinessApplication",
      applicationSubCategory: "Marketing automation / white-label SaaS for agencies",
      operatingSystem: "Web-based",
      url: SITE_URL,
      publisher: { "@id": `${SITE_URL}/#organization` },
      description:
        "All-in-one, white-label AI automation platform for agencies: Google Business Profile, Facebook, Instagram, WhatsApp API CRM and WordPress automation, AEO/GEO optimization, QR review capture, digital menus, bio links, instant websites and appointment scheduling - one multi-client dashboard, resold under the agency's own brand.",
      offers: {
        "@type": "AggregateOffer",
        priceCurrency: "INR",
        lowPrice: PLAN_LOW_PRICE,
        offerCount: 3,
        url: `${SITE_URL}/pricing`,
      },
      // TODO: needs real data - add `aggregateRating` / `review` only when
      // verified rating values and counts exist. Do not use placeholder numbers.
    },
  ],
};

const Landing = () => (
  <div className="min-h-screen bg-[#F4F6FB]" data-testid="landing-page">
    <SEO
      title="MagicBolt AI - Launch Your Own White-Label SaaS for Agencies"
      description="Agency OS by MagicBolt AI: a white-label SaaS bundling Google Business Profile, Facebook, Instagram and WordPress automation - your brand, your prices."
      path="/"
    />
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(ORGANIZATION_JSON_LD)}</script>
    </Helmet>
    <Navbar />
    <main>
      <Hero />
      <FeatureGrid />
      <WhiteLabel />
      <HowAgenciesEarn />
      <FAQ />
      <CredibilityStrip />
    </main>
    <Footer />
  </div>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollManager />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/features/:slug" element={<FeatureDetailPage />} />
          <Route path="/agency-os" element={<WhiteLabelPage />} />
          <Route path="/how-agencies-earn" element={<HowAgenciesEarnPage />} />
          <Route path="/start" element={<ExamineCustomerPage />} />
          <Route path="/white-label" element={<Navigate to="/agency-os" replace />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/product-finder" element={<ProductFinderPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/pricing/:slug" element={<PricingPlanDetailPage />} />
          <Route path="/plans" element={<PlansPage />} />
          <Route path="/plans/:id" element={<PlanDetailPage />} />
          <Route path="/white-label-ai-saas-lifetime-deal" element={<LifetimeDealPage />} />
          <Route path="/lifetime-deal" element={<Navigate to="/white-label-ai-saas-lifetime-deal" replace />} />
          {/* <Route path="/signup" element={<SignupPage />} /> */}
          {/* <Route path="/login" element={<LoginPage />} /> */}
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-conditions" element={<TermsPage />} />
          <Route path="/refund-policy" element={<RefundPolicyPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <WhatsAppButton />
        <CookieConsent />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
